import { Box, Divider, Skeleton, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { Report } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { REPORT_TITLES } from '@/components/Report/consts';
import { useGetLatestDuplicationReport } from '@/hooks/api/reports/useGetLatestDuplicationReport';
import { useTenants } from '@/services/tenants/useTenants';

const SummaryLink = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 8px',
  borderRadius: '6px',
  alignSelf: 'flex-start',
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },
}));

const Marker = styled('span')(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '2px',
  flexShrink: 0,
  transform: 'rotate(45deg)',
  backgroundColor: theme.palette.secondary.main,
}));

const summaryBody = ({
  report,
  isLoading,
  isError,
  listedReportIds,
  getPath,
}: {
  report?: Report;
  isLoading: boolean;
  isError: boolean;
  listedReportIds: string[];
  getPath: (path: string) => string;
}) => {
  if (isError) return <BodyText color='text.secondary'>Failed to load the duplication summary.</BodyText>;

  if (isLoading) return <Skeleton variant='rounded' height={32} />;

  if (!report) {
    return (
      <Box display='flex' alignItems='center' gap='8px' data-testid='no-duplication-summary'>
        <BodyText color='text.secondary'>No duplication report yet.</BodyText>
        <SummaryLink to={getPath('report')}>
          <BodyText color='secondary.main'>Run one to find duplicate groups</BodyText>
        </SummaryLink>
      </Box>
    );
  }

  // A listed report already carries its own count, so repeating it under the tile says nothing new.
  // The line is only worth its space when the newest completed duplication report fell off the list.
  if (listedReportIds.includes(report.id)) return;

  const { groupCount } = report;
  const groups = `${groupCount.toLocaleString()} duplicate ${groupCount === 1 ? 'group' : 'groups'}`;

  return (
    <SummaryLink to={getPath(`report/${report.id}`)} title='View the latest duplication report'>
      <Marker />
      <BodyText data-testid='duplication-summary'>
        {`${report.name || REPORT_TITLES[report.reportType]} found ${groups}`}
      </BodyText>
    </SummaryLink>
  );
};

export const DuplicationSummarySection = ({
  pollWhileRunning,
  listedReportIds,
}: {
  pollWhileRunning?: boolean;
  listedReportIds: string[];
}) => {
  const { getPath } = useTenants();
  const { data: report, isLoading, isError } = useGetLatestDuplicationReport({ pollWhileRunning });

  const body = summaryBody({ report, isLoading, isError, listedReportIds, getPath });

  // The rule goes with the line it separates, rather than dangling under the last row.
  if (!body) return;

  return (
    <>
      <Divider />
      {body}
    </>
  );
};
